import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType>({ toast: () => {} });

export const useToast = () => useContext(ToastContext);

const ICONS = {
  success: <CheckCircle size={16} color="#22c55e" />,
  error: <XCircle size={16} color="#ef4444" />,
  info: <AlertCircle size={16} color="#3b82f6" />,
};

const BG = {
  success: '#f0fdf4',
  error: '#fef2f2',
  info: '#eff6ff',
};

const BORDER = {
  success: '#bbf7d0',
  error: '#fecaca',
  info: '#bfdbfe',
};

let _id = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++_id;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const dismiss = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360,
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '12px 14px', borderRadius: 10,
            background: BG[t.type], border: `1px solid ${BORDER[t.type]}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            animation: 'slideIn 0.2s ease',
            fontSize: 13, color: '#1e293b',
          }}>
            <div style={{ flexShrink: 0, marginTop: 1 }}>{ICONS[t.type]}</div>
            <div style={{ flex: 1 }}>{t.message}</div>
            <button onClick={() => dismiss(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8', flexShrink: 0 }}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
